import type React from 'react';
import {useEffect, useRef} from 'react';
import {useInView} from 'react-intersection-observer';
import {useInfiniteCompanyList} from '../../../../core/repositories/companyRepository';
import {sortWorkAttractiveConditions} from '../../usecase/sortWorkAttractiveConditions';
import {COMPANY_LIST_LIMIT} from './constants';
import {CompaniesFramePresentation} from './presentation';

type Props = {
  componentID: string;
  item: {
    id: number;
    name: string;
  };
  filterQuery: {
    limit?: number;
    industryIDs?: number[];
    industryCategoryIDs?: number[];
    minEmployeeCount?: number;
    maxEmployeeCount?: number;
    workAttractiveConditionIDs?: number[];
  };
  handleSetTotalCount?: (key: string, count: number) => void;
  isConsultant: boolean;
};

export const CompaniesFrameContainer: React.FC<Props> = ({
  componentID,
  item,
  filterQuery,
  handleSetTotalCount,
  isConsultant,
}) => {
  const {ref, inView} = useInView({
    triggerOnce: true,
  });
  const {data, isLoading: isLoadingCompanies} = useInfiniteCompanyList({
    queryParams: {
      industryIDs: filterQuery.industryIDs ?? [],
      industryCategoryIDs: filterQuery.industryCategoryIDs ?? [],
      limit: COMPANY_LIST_LIMIT,
      page: 1,
      order: 'pageType',
      cacheID: '',
      minEmployeeCount: filterQuery.minEmployeeCount,
      maxEmployeeCount: filterQuery.maxEmployeeCount,
      workAttractiveConditionIDs: filterQuery.workAttractiveConditionIDs ?? [],
    },
    config: {
      enabled: inView,
    },
  });

  const companies =
    data?.pages
      .flatMap((page) => page?.companies ?? [])
      .map((company) => ({
        ...company,
        // 絞り込み条件に合致するものを先頭に持ってくる
        workAttractiveConditions: sortWorkAttractiveConditions(
          company.workAttractiveConditions,
          filterQuery.workAttractiveConditionIDs ?? [],
        ),
      })) ?? [];
  const industryId =
    filterQuery.industryCategoryIDs &&
    filterQuery.industryCategoryIDs?.length > 0
      ? filterQuery.industryCategoryIDs[0]
      : 0;
  const industryPageUrl = isConsultant
    ? `/company/industry/${1}?industryIDs=${filterQuery.industryIDs?.join(',') ?? ''}`
    : `/company/industry/${industryId}`;

  const prevCompaniesLength = useRef(0);

  useEffect(() => {
    if (handleSetTotalCount) {
      const currentLength = companies.length;
      if (currentLength !== prevCompaniesLength.current) {
        handleSetTotalCount(componentID, currentLength);
        prevCompaniesLength.current = currentLength;
      }
    }
  }, [companies.length, handleSetTotalCount, componentID]);

  if (companies.length === 0 && !isLoadingCompanies) {
    return <></>;
  }

  return (
    <CompaniesFramePresentation
      ref={ref}
      inView={inView}
      componentID={componentID}
      item={item}
      isLoading={isLoadingCompanies}
      companies={companies}
      industryPageUrl={industryPageUrl}
    />
  );
};
